import React, { useMemo, useRef, useReducer, useState, useEffect, useCallback, memo } from 'react';
import {
  ArrowPathIcon,
  PencilIcon,
  EyeIcon,
  EyeSlashIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  DocumentMagnifyingGlassIcon,
  Cog6ToothIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useSyncActuals } from '../../../hooks/engagement/useEngagement';
import { formatCurrency } from '../../bids/utils/bidWizardHelpers';
import { documentsApi } from '../../../hooks/document/documentsApi';
import DataTable from '../../common/DataTable';
import ExtractionResultModal from '../documents/ExtractionResultModal';

const fmtMonth = (dateStr) => {
  if (!dateStr) return null;
  const d        = new Date(dateStr);
  const adjusted = new Date(d.getUTCFullYear(), d.getUTCMonth(), 1);
  return adjusted.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// ── Visibility Settings Panel ─────────────────────────────────────────────────
const VisibilityPanel = memo(({
  allEngagements,
  localHiddenIds,
  hasUnsavedChanges,
  isSaving,
  onClose,
  onToggle,
  onSelectAll,
  onSave,
  onCancel,
}) => {
  const allVisible  = localHiddenIds.size === 0;
  const someHidden  = localHiddenIds.size > 0 && localHiddenIds.size < allEngagements.length;

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">

      {/* Save / Cancel — only when dirty */}
      {hasUnsavedChanges && (
        <div className="p-3 border-b bg-blue-50">
          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={isSaving}
              className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="flex-1 px-2 py-1.5 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="px-3 py-2.5 border-b flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700">Engagement Visibility</span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">
            {allEngagements.length - localHiddenIds.size}/{allEngagements.length} visible
          </span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
        </div>
      </div>

      {/* Select All toggle */}
      <div className="px-3 pt-2 pb-1">
        <label className="flex items-center gap-2.5 px-2 py-1.5 bg-gray-50 rounded cursor-pointer hover:bg-gray-100">
          <input
            type="checkbox"
            checked={allVisible}
            ref={(el) => { if (el) el.indeterminate = someHidden; }}
            onChange={onSelectAll}
            className="h-3.5 w-3.5 rounded"
          />
          <span className="text-xs font-medium text-gray-700">
            {allVisible ? 'Hide All' : 'Show All'}
          </span>
        </label>
      </div>

      {/* Engagement list */}
      <div className="max-h-64 overflow-y-auto px-3 pb-3 space-y-0.5">
        {allEngagements.map((eng) => {
          const isHidden = localHiddenIds.has(eng.id);
          return (
            <label
              key={eng.id}
              className="flex items-center gap-2.5 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 group"
            >
              <input
                type="checkbox"
                checked={!isHidden}
                onChange={() => onToggle(eng.id)}
                className="h-3.5 w-3.5 rounded shrink-0"
              />
              <span className={`text-xs truncate flex-1 ${isHidden ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                {eng.engagement_name || eng.qb_customer_name || eng.id}
              </span>
              {isHidden
                ? <EyeSlashIcon className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                : <EyeIcon className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
              }
            </label>
          );
        })}
      </div>
    </div>
  );
});

// ── Main Component ─────────────────────────────────────────────────────────────

const EngagementTable = ({
  engagements,          // already-filtered list from Step3
  allEngagements,       // full unfiltered list — used for the visibility panel
  filters,
  onUpdateFilter,
  onResetFilters,
  onEditEngagement,
  onSelectTemplate,
  onPreviewDocument,
  onDownloadDocument,
  onNextStep,
  onViewBreakdown,
  onUploadForEngagement,
  // Visibility props passed down from Step3
  hiddenIds,            // Set<string> — persisted hidden set
  onToggleHidden,       // (id) => void  — toggle one in local state
  onSelectAllHidden,    // () => void    — show/hide all
  onSaveVisibility,     // async () => void
  onCancelVisibility,   // () => void
  hasUnsavedVisibility, // boolean
  isSavingVisibility,   // boolean
  localHiddenIds,       // Set<string> — local (unsaved) state for panel
}) => {
  const syncMutation   = useSyncActuals();
  const syncingIdsRef  = useRef(new Set());
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const [extractionResult,    setExtractionResult]    = useState(null);
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const [loadingExtractionId, setLoadingExtractionId] = useState(null);
  const [showVisibilityPanel, setShowVisibilityPanel] = useState(false);

  const panelRef = useRef(null);

  // Close panel on outside click
  useEffect(() => {
    if (!showVisibilityPanel) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowVisibilityPanel(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showVisibilityPanel]);

  const handleViewExtraction = async (documentId) => {
    if (!documentId) return;
    setLoadingExtractionId(documentId);
    try {
      const response = await documentsApi.getExtractionResult(documentId);
      const payload =
        response?.data?.data ??
        response?.data     ??
        response;
      let data = payload?.extraction_result ?? payload;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { data = null; }
      }
      if (!data || (typeof data === 'object' && !Object.keys(data).length)) {
        alert('No extraction result found. Process the document first.');
        return;
      }
      setExtractionResult(data);
      setShowExtractionModal(true);
    } catch (err) {
      console.error('View extraction failed:', err);
      alert('No extraction result found. Process the document first.');
    } finally {
      setLoadingExtractionId(null);
    }
  };

  const handleSync = async (row) => {
    if (!row.qb_customer_ref) return;
    syncingIdsRef.current.add(row.id);
    forceUpdate();
    try {
      await syncMutation.mutateAsync(row.id);
    } finally {
      syncingIdsRef.current.delete(row.id);
      forceUpdate();
    }
  };

  // Filter out hidden engagements
  const visibleEngagements = useMemo(
    () => engagements.filter((e) => !hiddenIds.has(e.id)),
    [engagements, hiddenIds],
  );

  const hiddenCount = hiddenIds.size;

  const columns = useMemo(() => [
    {
      key:        'engagement_name',
      label:      'Engagement Name',
      searchable: true,
      render: (value, row) => (
        <div className="whitespace-normal break-words max-w-xs">
          <div className="font-medium text-slate-900">{value}</div>
          {row.qb_customer_name && (
            <div className="text-xs text-slate-500">{row.qb_customer_name}</div>
          )}
          {/* Show ALL tags — no truncation */}
          {row.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {row.tags.map((tag) => (
                <span key={tag} className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      key:        'document_id',
      label:      'Document',
      sortable:   false,
      searchable: false,
      render: (value, row) => {
        if (!value) return <span className="text-xs text-slate-300">—</span>;
        const isLoadingThis = loadingExtractionId === value;
        return (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPreviewDocument(value)}
              className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
              title="Preview document"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleViewExtraction(value)}
              disabled={isLoadingThis}
              className="p-1.5 text-amber-500 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-40"
              title="View AI extraction"
            >
              {isLoadingThis
                ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
                : <DocumentMagnifyingGlassIcon className="w-4 h-4" />
              }
            </button>
          </div>
        );
      },
    },
    {
      key:        'bid_amount',
      label:      'Bid Amount',
      searchable: false,
      render: (value) =>
        value != null ? (
          <div className="text-sm font-medium text-slate-900">{formatCurrency(value)}</div>
        ) : (
          <span className="text-slate-400 text-sm">—</span>
        ),
    },
    {
      key:        'start_date',
      label:      'Period',
      searchable: false,
      render: (value, row) => {
        const periodStart = fmtMonth(value);
        const periodEnd   = fmtMonth(row.end_date);
        return periodStart || periodEnd ? (
          <div className="text-sm text-slate-600 whitespace-nowrap">
            {periodStart || '?'} – {periodEnd || 'Present'}
          </div>
        ) : (
          <span className="text-slate-400 text-sm">—</span>
        );
      },
    },
    {
      key:        'actual_revenue',
      label:      'Revenue To Date',
      searchable: false,
      render: (value, row) => {
        const isRetainer   = row.engagement_type === 'Retainer';
        const displayValue = isRetainer ? row.retainer_monthly_value : row.actual_revenue;
        const label        = isRetainer ? 'avg/mo' : 'total';
        return displayValue != null ? (
          <div>
            <div className="text-sm font-medium text-slate-900">{formatCurrency(displayValue)}</div>
            <div className="text-xs text-slate-400">{label}</div>
          </div>
        ) : (
          <span className="text-slate-400 text-sm">—</span>
        );
      },
    },
    {
      key:        'profit_margin',
      label:      'Profit Margin',
      searchable: false,
      render: (value, row) =>
        value != null ? (
          <div className="flex items-center gap-1">
            <span className={`font-medium text-sm ${
              value > 30 ? 'text-green-600' : value > 20 ? 'text-blue-600' : 'text-amber-600'
            }`}>
              {value.toFixed(1)}%
            </span>
            {row.has_missing_rates && (
              <span title="Some roles have missing cost rates — margin may be overstated">
                <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
              </span>
            )}
          </div>
        ) : (
          <span className="text-slate-400 text-sm">—</span>
        ),
    },
    {
      key:        'id',
      label:      'Actions',
      sortable:   false,
      searchable: false,
      align:      'right',
      render: (_, row) => {
        const isSyncing = syncingIdsRef.current.has(row.id);
        return (
          <div className="flex items-center justify-end gap-1">
            {/* Upload document for this engagement */}
            <button
              onClick={() => onUploadForEngagement?.(row.id, row.engagement_name || row.qb_customer_name)}
              className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
              title="Upload document"
            >
              <DocumentArrowUpIcon className="w-4 h-4" />
            </button>

            {/* Sync actuals from QuickBooks */}
            {row.qb_customer_ref && (
              <button
                onClick={() => handleSync(row)}
                disabled={isSyncing}
                className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
                title={isSyncing ? 'Syncing…' : 'Sync actuals from QuickBooks'}
              >
                <ArrowPathIcon className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
              </button>
            )}

            <button
              onClick={() => onEditEngagement(row)}
              className="p-1.5 text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
              title="Edit"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              data-tour="step3-use-template"
              onClick={() => { onSelectTemplate(row); onNextStep(); }}
              className="p-1.5 text-brightblue-600 hover:bg-brightblue-50 rounded-lg transition-colors"
              title="Use as Template"
            >
              <ClipboardDocumentCheckIcon className="w-4 h-4" />
            </button>
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [onEditEngagement, onSelectTemplate, onNextStep, onViewBreakdown, onPreviewDocument, loadingExtractionId, onUploadForEngagement]);

  return (
    <>
      {/* ── Gear icon row — sits above the DataTable ── */}
      <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100">
        <p className="text-xs text-gray-400">
          {visibleEngagements.length} engagement{visibleEngagements.length !== 1 ? 's' : ''}
          {hiddenCount > 0 && (
            <span className="ml-1 text-gray-400">
              ({hiddenCount} hidden)
            </span>
          )}
        </p>

        {/* Gear button + panel */}
        <div className="relative" ref={panelRef}>
          <button
            onClick={() => setShowVisibilityPanel((v) => !v)}
            className={`relative p-1.5 rounded-lg transition-colors ${
              showVisibilityPanel
                ? 'bg-gray-100 text-gray-700'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
            }`}
            title="Visibility settings"
          >
            <Cog6ToothIcon className="w-4 h-4" />
            {hiddenCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                {hiddenCount}
              </span>
            )}
          </button>

          {showVisibilityPanel && (
            <VisibilityPanel
              allEngagements={allEngagements}
              localHiddenIds={localHiddenIds}
              hasUnsavedChanges={hasUnsavedVisibility}
              isSaving={isSavingVisibility}
              onClose={() => setShowVisibilityPanel(false)}
              onToggle={onToggleHidden}
              onSelectAll={onSelectAllHidden}
              onSave={async () => {
                await onSaveVisibility();
                setShowVisibilityPanel(false);
              }}
              onCancel={() => {
                onCancelVisibility();
                setShowVisibilityPanel(false);
              }}
            />
          )}
        </div>
      </div>

      <DataTable
        data={visibleEngagements}
        columns={columns}
        defaultSort={{ key: 'engagement_name', direction: 'asc' }}
        defaultItemsPerPage={25}
        showSearch={false}
        showFilters={false}
        showPagination={true}
        emptyMessage={
          hiddenCount > 0 && visibleEngagements.length === 0
            ? `All engagements are hidden. Open settings to restore visibility.`
            : 'No Won engagements found.'
        }
      />

      {showExtractionModal && extractionResult && (
        <ExtractionResultModal
          result={extractionResult}
          onClose={() => { setShowExtractionModal(false); setExtractionResult(null); }}
        />
      )}
    </>
  );
};

export default EngagementTable;